import { Button, Form, Modal, Upload, message, UploadFile } from "antd";
import React, { useEffect } from "react";
import ImgCrop from "antd-img-crop";
import { useMutation } from "~/hooks";
import {
  CURRENT_PDF_OPTIONS,
  DELETE_PDF_OPTION,
  UPLOAD_FOOTER_IMAGE,
  UPLOAD_HEADER_IMAGE,
  UPLOAD_PDF_ATTACHMENT,
} from "./graphql-queries";
import { useQuery } from "@apollo/client";
import { UploadOutlined } from "@ant-design/icons";
import { newAlertMessage, showAutoClosingAlert } from "~/design-system";
import { RcFile } from "antd/lib/upload";

type HeaderFooterModalProps = {
  outputDesignId: number;
  open: boolean;
  onClose: () => void;
  onOk: () => void;
};

export const PdfHeaderFooterModal = ({
  outputDesignId,
  open,
  onClose,
  onOk,
}: HeaderFooterModalProps) => {
  const { data: currentPdfOptions } = useQuery(CURRENT_PDF_OPTIONS, {
    variables: {
      outputDesignId,
    },
  });

  const [uploadHeaderImage] = useMutation(UPLOAD_HEADER_IMAGE);
  const [uploadFooterImage] = useMutation(UPLOAD_FOOTER_IMAGE);
  const [uploadPdfAttachment] = useMutation(UPLOAD_PDF_ATTACHMENT);
  const [deletePdfOption] = useMutation(DELETE_PDF_OPTION);

  const checkIfFileExistsBeforeUpload = (
    file: RcFile,
    type: "headerImage" | "footerImage" | "contentBefore" | "contentAfter"
  ) => {
    const currentFileName = currentPdfOptions?.currentPdfOptions?.[`${type}Name`];
    if (currentFileName === file.name) {
      showAutoClosingAlert(newAlertMessage("error", "File already exists"));
      return Promise.reject(new Error("File already exists"));
    }
    return true;
  };

  const [form] = Form.useForm();

  useEffect(() => {
    if (currentPdfOptions) {
      form.setFieldsValue(currentPdfOptions);
    }
  }, [currentPdfOptions]);

  return (
    <Modal onOk={() => onOk()} open={open} onCancel={() => onClose()}>
      <Form style={{ width: "100%", marginTop: 10 }} form={form}>
        <Form.Item
          getValueFromEvent={(img) => {
            return {
              value: img || null,
            };
          }}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          name="headerImageName"
          label="Header"
          labelAlign="left"
        >
          <ImgCrop
            quality={1}
            modalTitle="Crop Image"
            modalOk="Crop"
            zoom
            modalCancel="Cancel"
            modalWidth={800}
            aspect={21 / 3}
            onUploadFail={(error) => message.error(`Upload failed: ${error.message}`)}
            minZoom={0.1} // Allow zooming out to 10% of original size
            maxZoom={3} // Allow zooming in to 300% of original size
            shape="rect"
            grid={true}
          >
            <Upload
              beforeUpload={(file) => checkIfFileExistsBeforeUpload(file, "headerImage")}
              onRemove={() => {
                deletePdfOption({
                  variables: {
                    option: "headerImageUrl",
                    outputDesignId,
                  },
                  update: (cache) => {
                    cache.updateQuery(
                      {
                        query: CURRENT_PDF_OPTIONS,
                        variables: { outputDesignId },
                      },
                      (data) => {
                        if (data) {
                          return {
                            currentPdfOptions: {
                              ...data.currentPdfOptions,
                              headerImageName: null,
                            },
                          };
                        }
                        return data;
                      }
                    );
                  },
                });
              }}
              multiple={false}
              defaultFileList={
                currentPdfOptions?.currentPdfOptions?.headerImageName
                  ? [
                      {
                        uid: "2",
                        name: currentPdfOptions?.currentPdfOptions?.headerImageName ?? "",
                      },
                    ]
                  : []
              }
              onChange={async (uc) => {
                const firstFile =
                  (uc.fileList[0] as UploadFile) ||
                  ({ percent: 0, status: "removed", originFileObj: {} } as UploadFile);
                const {
                  percent: firstFilePercent,
                  status: firstFileStatus,
                  originFileObj,
                } = firstFile;

                if (0 === firstFilePercent && "uploading" === firstFileStatus) {
                  const file = originFileObj as File;
                  uploadHeaderImage({
                    variables: {
                      headerInput: {
                        headerImage: file,
                        outputDesignId,
                      },
                    },
                    context: { hasUpload: true },
                    update: (cache) => {
                      cache.updateQuery(
                        {
                          query: CURRENT_PDF_OPTIONS,
                          variables: { outputDesignId },
                        },
                        (data) => {
                          if (data) {
                            return {
                              currentPdfOptions: {
                                ...data.currentPdfOptions,
                                headerImageName: file.name,
                              },
                            };
                          }
                          return data;
                        }
                      );
                    },
                  });
                }
              }}
              accept=".png,.jpg,.jpeg"
            >
              <div>
                <Button icon={<UploadOutlined />}>Upload</Button>
              </div>
            </Upload>
          </ImgCrop>
        </Form.Item>
        <Form.Item
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          getValueFromEvent={(img) => {
            return {
              existingImage: null,
              value: img || null,
            };
          }}
          name="footerImageName"
          label="Footer"
          labelAlign="left"
        >
          <ImgCrop
            quality={1}
            modalTitle="Crop Image"
            modalOk="Crop"
            zoom
            modalCancel="Cancel"
            modalWidth={800}
            aspect={21 / 3}
            onUploadFail={(error) => message.error(`Upload failed: ${error.message}`)}
            minZoom={0.1} // Allow zooming out to 10% of original size
            maxZoom={3} // Allow zooming in to 300% of original size
            shape="rect"
            grid={true}
          >
            <Upload
              beforeUpload={(file) => checkIfFileExistsBeforeUpload(file, "footerImage")}
              onRemove={() => {
                deletePdfOption({
                  variables: {
                    option: "footerImageUrl",
                    outputDesignId,
                  },
                  update: (cache) => {
                    cache.updateQuery(
                      {
                        query: CURRENT_PDF_OPTIONS,
                        variables: { outputDesignId },
                      },
                      (data) => {
                        if (data) {
                          return {
                            currentPdfOptions: {
                              ...data.currentPdfOptions,
                              footerImageName: null,
                            },
                          };
                        }
                        return data;
                      }
                    );
                  },
                });
              }}
              multiple={false}
              defaultFileList={
                currentPdfOptions?.currentPdfOptions?.footerImageName
                  ? [
                      {
                        uid: "1",
                        name: currentPdfOptions?.currentPdfOptions?.footerImageName ?? "",
                      },
                    ]
                  : []
              }
              onChange={async (uc) => {
                const firstFile =
                  (uc.fileList[0] as UploadFile) ||
                  ({ percent: 0, status: "removed", originFileObj: {} } as UploadFile);
                const {
                  percent: firstFilePercent,
                  status: firstFileStatus,
                  originFileObj,
                } = firstFile;

                if (0 === firstFilePercent && "uploading" === firstFileStatus) {
                  const file = originFileObj as File;
                  uploadFooterImage({
                    variables: {
                      footerInput: {
                        footerImage: file,
                        outputDesignId,
                      },
                    },
                    context: { hasUpload: true },
                    update: (cache) => {
                      cache.updateQuery(
                        {
                          query: CURRENT_PDF_OPTIONS,
                          variables: { outputDesignId },
                        },
                        (data) => {
                          if (data) {
                            return {
                              currentPdfOptions: {
                                ...data.currentPdfOptions,
                                contentBeforeName: file.name,
                              },
                            };
                          }
                          return data;
                        }
                      );
                    },
                  });
                }
              }}
              accept=".png,.jpg,.jpeg"
            >
              <div>
                <Button icon={<UploadOutlined />}>Upload</Button>
              </div>
            </Upload>
          </ImgCrop>
        </Form.Item>
        <Form.Item
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          labelAlign="left"
          getValueFromEvent={(img) => {
            return {
              existingImage: null,
              value: img || null,
            };
          }}
          name="contentBeforeName"
          label="PDF Content Before"
        >
          <Upload
            beforeUpload={(file) => checkIfFileExistsBeforeUpload(file, "contentBefore")}
            multiple={false}
            defaultFileList={
              currentPdfOptions?.currentPdfOptions?.contentBeforeName
                ? [
                    {
                      uid: "3",
                      name: currentPdfOptions?.currentPdfOptions?.contentBeforeName ?? "",
                    },
                  ]
                : []
            }
            onRemove={() => {
              deletePdfOption({
                variables: {
                  option: "contentBeforeUrl",
                  outputDesignId,
                },
                update: (cache) => {
                  cache.updateQuery(
                    {
                      query: CURRENT_PDF_OPTIONS,
                      variables: { outputDesignId },
                    },
                    (data) => {
                      if (data) {
                        return {
                          currentPdfOptions: {
                            ...data.currentPdfOptions,
                            contentBeforeName: null,
                          },
                        };
                      }
                      return data;
                    }
                  );
                },
              });
            }}
            onChange={async (uc) => {
              const firstFile =
                (uc.fileList[0] as UploadFile) ||
                ({ percent: 0, status: "removed", originFileObj: {} } as UploadFile);
              const {
                percent: firstFilePercent,
                status: firstFileStatus,
                originFileObj,
              } = firstFile;

              if (0 === firstFilePercent && "uploading" === firstFileStatus) {
                const file = originFileObj as File;
                uploadPdfAttachment({
                  variables: {
                    pdfAttachment: {
                      pdfAttachment: file,
                      outputDesignId,
                      before: true,
                    },
                  },
                  context: { hasUpload: true },
                  update: (cache) => {
                    cache.updateQuery(
                      {
                        query: CURRENT_PDF_OPTIONS,
                        variables: { outputDesignId },
                      },
                      (data) => {
                        if (data) {
                          return {
                            currentPdfOptions: {
                              ...data.currentPdfOptions,
                              contentAfterName: file.name,
                            },
                          };
                        }
                        return data;
                      }
                    );
                  },
                });
              }
            }}
            accept=".pdf"
          >
            <div>
              <Button icon={<UploadOutlined />}>Upload</Button>
            </div>
          </Upload>
        </Form.Item>
        <Form.Item
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          labelAlign="left"
          getValueFromEvent={(img) => {
            return {
              existingImage: null,
              value: img || null,
            };
          }}
          name="contentAfterName"
          label="PDF Content After"
        >
          <Upload
            multiple={false}
            defaultFileList={
              currentPdfOptions?.currentPdfOptions?.contentAfterName
                ? [
                    {
                      uid: "4",
                      name: currentPdfOptions?.currentPdfOptions?.contentAfterName ?? "",
                    },
                  ]
                : []
            }
            onRemove={() => {
              deletePdfOption({
                variables: {
                  option: "contentAfterUrl",
                  outputDesignId,
                },
                update: (cache) => {
                  cache.updateQuery(
                    {
                      query: CURRENT_PDF_OPTIONS,
                      variables: { outputDesignId },
                    },
                    (data) => {
                      if (data) {
                        return {
                          currentPdfOptions: {
                            ...data.currentPdfOptions,
                            contentAfterName: null,
                          },
                        };
                      }
                      return data;
                    }
                  );
                },
              });
            }}
            onChange={async (uc) => {
              const firstFile =
                (uc.fileList[0] as UploadFile) ||
                ({ percent: 0, status: "removed", originFileObj: {} } as UploadFile);
              const {
                percent: firstFilePercent,
                status: firstFileStatus,
                originFileObj,
              } = firstFile;

              if (0 === firstFilePercent && "uploading" === firstFileStatus) {
                const file = originFileObj as File;
                uploadPdfAttachment({
                  variables: {
                    pdfAttachment: {
                      pdfAttachment: file,
                      outputDesignId,
                      after: true,
                    },
                  },
                  context: { hasUpload: true },
                  update: (cache) => {
                    cache.updateQuery(
                      {
                        query: CURRENT_PDF_OPTIONS,
                        variables: { outputDesignId },
                      },
                      (data) => {
                        if (data) {
                          return {
                            currentPdfOptions: {
                              ...data.currentPdfOptions,
                              contentAfterName: file.name,
                            },
                          };
                        }
                        return data;
                      }
                    );
                  },
                });
              }
            }}
            accept=".pdf"
          >
            <div>
              <Button icon={<UploadOutlined />}>Upload</Button>
            </div>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
